/**
 * 익명(비로그인) /chat 대화의 최근 기록을 서버로 보낼 모양으로 만든다.
 *
 * 익명 대화는 서버 메모리에만 있어 다음 턴이 다른 서버 워커로 가면 앞 대화가 사라진다.
 * 브라우저가 가진 기록을 함께 보내면 서버가 메모리 세션이 비었거나 뒤처졌을 때만 그걸로 잇는다.
 * 서버(services/chat_anonymous_history.py)도 같은 한도로 다시 자르고 PIIGuard 로 가린다.
 */
import { maskCriticalPII } from './piiMask';
import { stripLeakedCitationMarkers } from './chatArtifactTags';
import { answerTextForModel } from './chatRichCatalog';

export const ANON_HISTORY_MAX_MESSAGES = 10;
export const ANON_HISTORY_USER_CHAR_LIMIT = 2000;
export const ANON_HISTORY_ASSISTANT_CHAR_LIMIT = 4000;

const usableText = (message) => {
  if (!message || (message.role !== 'user' && message.role !== 'assistant')) return '';
  if (message.streaming || message.error) return '';
  const content = String(message.content || '');
  return answerTextForModel(
    message.role === 'assistant' ? stripLeakedCitationMarkers(content) : content,
    message.rich_blocks,
  ).trim();
};

/**
 * @param {Array} messages 이번 메시지를 보내기 전 화면의 메시지 목록
 * @returns {{ history: Array<{role: string, content: string}>, priorUserTurns: number, newlyMaskedCount: number }}
 *   newlyMaskedCount: 이번에 처음 기록에 들어가는 직전 주고받기(마지막 사용자·AI 메시지)에서 가린 개수.
 *   매 턴 같은 안내가 반복되지 않게 토스트는 이 값으로만 띄운다.
 */
export const buildAnonymousChatHistory = (messages = []) => {
  const list = Array.isArray(messages) ? messages : [];
  const priorUserTurns = list.filter((message) => message?.role === 'user').length;
  const usable = list
    .map((message) => ({ role: message?.role, content: usableText(message) }))
    .filter((item) => item.content);
  const recent = usable.slice(-ANON_HISTORY_MAX_MESSAGES);

  let lastUserIndex = -1;
  let lastAssistantIndex = -1;
  recent.forEach((item, index) => {
    if (item.role === 'user') lastUserIndex = index;
    else lastAssistantIndex = index;
  });

  let newlyMaskedCount = 0;
  const history = recent.map((item, index) => {
    const limit = item.role === 'user' ? ANON_HISTORY_USER_CHAR_LIMIT : ANON_HISTORY_ASSISTANT_CHAR_LIMIT;
    const { text, count } = maskCriticalPII(item.content);
    if (index === lastUserIndex || index === lastAssistantIndex) newlyMaskedCount += count;
    return { role: item.role, content: text.slice(0, limit) };
  });

  return { history, priorUserTurns, newlyMaskedCount };
};

export default buildAnonymousChatHistory;
